import { DomEventDispatcher } from '@frontmeans/dom-events';
import { cxDynamic, cxScoped, cxSingle, cxRecent } from '@proc7ts/context-values';
import { OnEvent__symbol, AfterEvent__symbol, trackValue, onAny, onSupplied, mapAfter, isAfterEvent, afterThe, translateAfter_, afterAll, onEventBy, valueOn_, supplyOn, EventEmitter, shareOn, mapOn_, eventReceiver, EventNotifier, resolveOnOrdered, digOn_, onceAfter } from '@proc7ts/fun-events';
import { noop, mergeFunctions, setOfElements, valueByRecipe, asis, valueProvider } from '@proc7ts/primitives';
import { BootstrapWindow, FeatureDef__symbol, BootstrapContext, ComponentRenderScheduler, Wesib__NS, ComponentContext, ComponentMember } from '@wesib/wesib';
import { itsEach, flatMapIt, overIterator, itsEvery, mapIt, filterArray, filterIt, overArray, itsFirst } from '@proc7ts/push-iterator';
import { drekCssClassesOf, deriveDrekContext, drekContextOf } from '@frontmeans/drek';
import { css__naming, NamespaceAliaser } from '@frontmeans/namespace-aliaser';
import { Supply, neverSupply } from '@proc7ts/supply';
import { cxFetchAgent, HttpFetch, FragmentRenderCtl } from '@wesib/generic';
import { hthvQuote, hthvParse } from '@hatsy/http-header-value';
import { importNode, importNodeContent } from '@frontmeans/dom-primitives';

/**
 * Extracts URL contained in the hash of another URL.
 *
 * Extracted hash URL is based on origin of the original URL. Still, it may be absolute one.
 *
 * Input URL is never altered.
 *
 * @param url - An URL to extract hash from.
 *
 * @returns URL extracted from hash.
 */
function getHashURL(url) {
    return new URL(url.hash.substring(1), url.origin);
}
/**
 * Creates an URL based on original one, but with hash substituted by the given hash URL.
 *
 * The hash URL is substituted as following:
 *
 * 1. If the hash URL's origin is not the same as the one of the base one, or if it contains a username,
 *    then absolute hash URL is substituted:
 * 2. Otherwise:
 *    - pathname is substituted, unless it is equal to `/` and no hash and search parameters present,
 *    - search parameters are substituted when at least one present,
 *    - hash is substituted only when present.
 *
 * Input URLs are never altered.
 *
 * @param url - Base URL.
 * @param hashURL - Hash URL to substitute.
 *
 * @returns URL with updated hash.
 */
function setHashURL(url, hashURL) {
    if (hashURL.origin !== url.origin || hashURL.username) {
        return new URL(`#${hashURL}`, url);
    }
    const { pathname, search, hash } = hashURL;
    const result = new URL('', url);
    result.hash = (search || hash || pathname.length > 1) ? (pathname + search + hash) : (search + hash);
    return result;
}

/**
 * A key of {@link PageParam.Ref page parameter request} property containing requested page parameter.
 */
const PageParam__symbol = ( /*#__PURE__*/Symbol('PageParam'));
/**
 * Page navigation parameter.
 *
 * Can applied before navigation happened (i.e. to {@link LeavePageEvent}). Then it will be available to the target page
 * both before and after navigation.
 *
 * @typeParam T - Parameter value type.
 * @typeParam TInput - Parameter input type.
 */
class PageParam {
    get [PageParam__symbol]() {
        return this;
    }
    /**
     * Creates default page parameter handle.
     *
     * This method is called when {@link Page.get requesting page parameter} which value is not present in the page.
     * The value handle returned is assigned to the page.
     *
     * Returns nothing by default.
     *
     * @param _page - A page to assign navigation parameter to.
     * @param _bsContext - Bootstrap context.
     *
     * @returns New page parameter value handle or nothing if there is no default value.
     */
    byDefault(_page, _bsContext) {
        return;
    }
}

class PageHashURLValueParam$ extends PageParam {
    create(page, input) {
        let hashURL;
        const handle = {
            get() {
                return hashURL;
            },
            put(value) {
                hashURL = typeof value === 'string' ? new URL(value, page.url.origin) : value;
            },
        };
        handle.put(input);
        return handle;
    }
}
/**
 * @internal
 */
const PageHashURLValueParam = (
/*#__PURE__*/ new PageHashURLValueParam$());

class PageHashURLParam$ extends PageParam {
    create(page, input) {
        const handle = {
            get() {
                return page.get(PageHashURLValueParam) || getHashURL(page.url);
            },
            put(value) {
                page.put(PageHashURLValueParam, value);
            },
        };
        handle.put(input);
        return handle;
    }
    byDefault(page) {
        return this.create(page, null);
    }
}
/**
 * Page parameter representing its {@link getHashURL hash URL}.
 *
 * When {@link Navigation.with set} to another value while navigating, the hash of target URL is updated, unless set to
 * `null` or `undefined`.
 *
 * Requires {@link PageHashURLSupport} for above to function properly.
 */
const PageHashURLParam = (
/*#__PURE__*/ new PageHashURLParam$());

/**
 * Context value entry containing {@link NavigationAgent} instance.
 *
 * The agent returned combines all registered agents into one. If no agent registered it just performs the navigation.
 */
const NavigationAgent = {
    perContext: ( /*#__PURE__*/cxDynamic({
        create: NavigationAgent$create,
        byDefault: _target => NavigationAgent$default,
        assign: ({ get, to }) => {
            const agent = (next, when, from, to) => get()(next, when, from, to);
            return receiver => to((_, by) => receiver(agent, by));
        },
    })),
    toString: () => '[NavigationAgent]',
};
function NavigationAgent$create(agents, target) {
    const { document } = target.get(BootstrapWindow);
    return NavigationAgent$combined;
    function NavigationAgent$combined(next, when, from, to) {
        return navigate(0, to);
        function navigate(agentIdx, agentTo) {
            const agent = agents[agentIdx];
            if (!agent) {
                return next(agentTo);
            }
            agent(({ url: nextURL = agentTo.url, title: nextTitle = agentTo.title, 
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            data: nextData = agentTo.data, } = agentTo) => navigate(agentIdx + 1, {
                url: new URL(String(nextURL), document.baseURI),
                title: nextTitle,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data: nextData,
                get visited() {
                    return agentTo.visited;
                },
                get current() {
                    return agentTo.current;
                },
                get(ref) {
                    return agentTo.get(ref);
                },
                put(ref, input) {
                    agentTo.put(ref, input);
                },
            }), when, from, agentTo);
        }
    }
}
function NavigationAgent$default(next, _when, _from, to) {
    next(to);
}

const PageHashURLSupport__feature = {
    setup(setup) {
        setup.provide({
            entry: NavigationAgent,
            placeAsset: (_target, collector) => collector(PageHashURL$agent),
        });
    },
};
/**
 * {@link PageHashURLParam Page hash URL parameter} support feature.
 */
class PageHashURLSupport {
    static get [FeatureDef__symbol]() {
        return PageHashURLSupport__feature;
    }
}
function PageHashURL$agent(next, _when, _from, to) {
    const hashURL = to.get(PageHashURLValueParam);
    if (hashURL) {
        next({ url: setHashURL(to.url, hashURL) });
    }
    else {
        next();
    }
}

const NAV_DATA_KEY = 'wesib:navigation:data';
function extractNavData(state) {
    return state == null || typeof state !== 'object'
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ? { data: state }
        : state[NAV_DATA_KEY];
}
const NavHistory$perContext = ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
    byDefault: target => new NavHistory(target.get(BootstrapContext)),
}))));
class NavHistory {
    constructor(_context) {
        this._context = _context;
        this._entries = new Map();
        this._lastId = 0;
        const window = _context.get(BootstrapWindow);
        this._document = window.document;
        this._location = window.location;
        this._history = window.history;
        this._uid = btoa(String(Math.random()));
    }
    static perContext(target) {
        return NavHistory$perContext(target);
    }
    static toString() {
        return '[NavHistory]';
    }
    init() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data } = extractNavData(this._history.state);
        const entry = this.newEntry({
            url: new URL(this._location.href),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            data,
            title: this._document.title,
        });
        this._entries.set(entry.id, entry);
        entry.schedule(() => {
            entry.enter('init');
            this._history.replaceState(this._historyState(entry), '');
        });
        return entry;
    }
    newEntry(target) {
        return new PageEntry(this._context, ++this._lastId, target);
    }
    open(toEntry, tracker) {
        const { page: { title = '', url } } = toEntry;
        this._history.pushState(this._historyState(toEntry), title, url.href);
        this._enter('open', toEntry, tracker);
    }
    _enter(when, toEntry, tracker) {
        const fromEntry = tracker.it;
        this._entries.set(toEntry.id, toEntry);
        try {
            // Forget all entries starting from next one
            for (let e = fromEntry.next; e; e = e.next) {
                this._forget(e);
            }
        }
        finally {
            toEntry.prev = fromEntry;
            fromEntry.next = toEntry;
            toEntry.schedule(() => {
                try {
                    fromEntry.leave();
                }
                finally {
                    toEntry.enter(when);
                }
            });
            tracker.it = toEntry;
        }
    }
    replace(toEntry, tracker) {
        const fromEntry = tracker.it;
        const { page: { title = '', url } } = toEntry;
        this._history.replaceState(this._historyState(toEntry), title, url.href);
        this._entries.set(toEntry.id, toEntry);
        const prev = fromEntry.prev;
        if (prev) {
            toEntry.prev = prev;
            prev.next = toEntry;
        }
        toEntry.schedule(() => {
            try {
                fromEntry.leave();
            }
            finally {
                try {
                    this._forget(fromEntry);
                }
                finally {
                    toEntry.enter('replace');
                }
            }
        });
        tracker.it = toEntry;
    }
    popState(popState, tracker) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { state } = popState;
        if (state == null) {
            // Hash change
            if (this._history.state == null) {
                // Not a return
                return this._changeHash(tracker);
            }
            return; // Already handled by `hashchange` handler
        }
        const fromEntry = tracker.it;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { uid, data, id: pageId } = extractNavData(state);
        let toEntry;
        const existingEntry = uid === this._uid && pageId != null ? this._entries.get(pageId) : undefined;
        if (existingEntry) {
            toEntry = existingEntry;
        }
        else {
            // Returning to page existed in previous app version
            toEntry = this.newEntry({
                url: new URL(this._location.href),
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data,
                title: this._document.title,
            });
            fromEntry.transfer(toEntry, 'return');
            this._entries.set(toEntry.id, toEntry);
            this._history.replaceState(this._historyState(toEntry), '');
        }
        toEntry.schedule(() => {
            try {
                fromEntry.leave();
            }
            finally {
                toEntry.enter('return');
            }
        });
        tracker.it = toEntry;
        return toEntry;
    }
    hashChange(tracker) {
        if (this._history.state == null) {
            // Not a return
            return this._changeHash(tracker);
        }
        // Otherwise, a `popstate` event is also triggered,
        // and its handler would do the job (or already did).
    }
    update(tracker, url) {
        const oldEntry = tracker.it;
        const newEntry = new PageEntry(this._context, ++this._lastId, { ...oldEntry.page, url }, oldEntry);
        this._entries.set(newEntry.id, newEntry);
        this._history.replaceState(this._historyState(newEntry), '', url.href);
        this._entries.delete(oldEntry.id);
        return tracker.it = newEntry;
    }
    _changeHash(tracker) {
        const fromEntry = tracker.it;
        const toEntry = this.newEntry({
            url: new URL(this._location.href),
            data: null,
            title: this._document.title,
        });
        try {
            fromEntry.transfer(toEntry, 'enter');
        }
        finally {
            this._history.replaceState(this._historyState(toEntry), '');
            this._enter('enter', toEntry, tracker);
        }
        return toEntry;
    }
    _forget(entry) {
        this._entries.delete(entry.id);
        entry.forget();
    }
    _historyState({ id, page: { data } }) {
        return {
            [NAV_DATA_KEY]: {
                uid: this._uid,
                id,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data,
            },
        };
    }
}
/**
 * @internal
 */
class PageEntry {
    constructor(_bsContext, id, target, proto) {
        this._bsContext = _bsContext;
        this.id = id;
        this._status = 0 /* New */;
        this._update = noop;
        this._params = proto ? proto._params : new Map();
        const entry = this;
        this.page = {
            get url() {
                return target.url;
            },
            get title() {
                return target.title;
            },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            get data() {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return target.data;
            },
            get visited() {
                return !!entry._status;
            },
            get current() {
                return entry._status === 2 /* Current */;
            },
            get(ref) {
                return entry.get(ref);
            },
            put(ref, input) {
                entry.put(ref, input);
            },
        };
    }
    get(ref) {
        const param = ref[PageParam__symbol];
        const handle = this._params.get(param);
        if (handle) {
            return handle.get();
        }
        const newHandle = param.byDefault(this.page, this._bsContext);
        return newHandle && this._init(param, newHandle);
    }
    put(ref, input) {
        const param = ref[PageParam__symbol];
        const handle = this._params.get(param);
        if (handle) {
            handle.put(input);
            return handle.get();
        }
        return this._init(param, param.create(this.page, input, this._bsContext));
    }
    _init(param, handle) {
        this._params.set(param, handle);
        if (this.page.current && handle.enter) {
            handle.enter(this.page, 'init');
        }
        return handle.get();
    }
    transfer(to, when) {
        itsEach(this._params.entries(), ([param, handle]) => {
            if (handle.transfer) {
                const transferred = handle.transfer(to.page, when);
                if (transferred) {
                    to._params.set(param, transferred);
                }
            }
        });
    }
    stay(at) {
        itsEach(this._params.values(), handle => handle.stay && handle.stay(at));
    }
    enter(when) {
        this._status = 2 /* Current */;
        itsEach(this._params.values(), handle => handle.enter && handle.enter(this.page, when));
    }
    leave() {
        this._status = 1 /* Visited */;
        itsEach(this._params.values(), handle => handle.leave && handle.leave());
    }
    forget() {
        itsEach(this._params.values(), handle => handle.forget && handle.forget());
        this._params.clear();
    }
    schedule(update) {
        this._update = update;
    }
    apply() {
        const update = this._update;
        this._update = noop;
        update();
    }
}

/**
 * An event fired when navigation target reached by entering destination page.
 *
 * @event EnterPageEvent#wesib:enterPage
 */
class EnterPageEvent extends Event {
    /**
     * Constructs enter page event.
     *
     * @param type - Event type.
     * @param init - Initialization options.
     */
    constructor(type, init) {
        super(type, { ...init, cancelable: false });
        this.when = init.when;
        this.to = init.to;
    }
}
/**
 * An event fired right before leaving the page in order to navigate to another one.
 *
 * This event can be cancelled in order to prevent actual navigation or history update. The navigation is also
 * cancelled when another navigation initiated by one of the handlers of this event.
 *
 * @event LeavePageEvent#wesib:leavePage
 */
class LeavePageEvent extends Event {
    /**
     * Constructs leave page event.
     *
     * @param type - Event type.
     * @param init - Initialization options.
     */
    constructor(type, init) {
        super(type, { ...init, cancelable: true });
        this.when = init.when;
        this.from = init.from;
        this.to = init.to;
    }
}
/**
 * An event fired when navigation cancelled or failed.
 *
 * @event StayOnPageEvent#wesib:stayOnPage
 */
class StayOnPageEvent extends Event {
    /**
     * Constructs stay on page event.
     *
     * @param type - Event type.
     * @param init - Initialization options.
     */
    constructor(type, init) {
        super(type, { ...init, cancelable: true });
        this.from = init.from;
        this.to = init.to;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.reason = init.reason;
    }
    /**
     * When navigation event occurred. Always `stay`.
     */
    get when() {
        return 'stay';
    }
}

const Navigation$perContext = ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
    byDefault: Navigation$byDefault,
}))));
/**
 * Browser navigation service.
 *
 * Expected to be used as a [History] and [Location] APIs replacement.
 *
 * Fires additional navigation events the browser does not support natively.
 *
 * Implements an `EventSender` interface by sending {@link NavigationEvent navigation events} to registered receivers.
 *
 * Implements an `EventKeeper` interface by sending current {@link Page page} to registered receivers.
 *
 * Available as bootstrap context value.
 *
 * [History]: https://developer.mozilla.org/en-US/docs/Web/API/History
 * [Location]: https://developer.mozilla.org/en-US/docs/Web/API/Location
 */
class Navigation {
    static perContext(target) {
        return Navigation$perContext(target);
    }
    static toString() {
        return '[Navigation]';
    }
    [OnEvent__symbol]() {
        return this.on;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
    /**
     * Goes to the previous page in navigation history.
     *
     * Calling this method is the same as calling `go(-1)`.
     */
    back() {
        this.go(-1);
    }
    /**
     * Goes to the next page in navigation history.
     *
     * Calling this method is the same as calling `go(1)`.
     */
    forward() {
        this.go(1);
    }
    /**
     * Reloads current page.
     *
     * Calling this method is the same as calling `go()`.
     */
    reload() {
        this.go();
    }
}
function Navigation$byDefault(target) {
    const window = target.get(BootstrapWindow);
    const { document, history } = window;
    const dispatcher = new DomEventDispatcher(window);
    const navHistory = target.get(NavHistory);
    const agent = target.get(NavigationAgent);
    const nav = trackValue(navHistory.init());
    nav.read(nextEntry => nextEntry.apply()); // The very first page entry receiver applies scheduled updates to page
    let next = Promise.resolve();
    dispatcher.on('popstate')(popState => {
        const entry = navHistory.popState(popState, nav);
        if (entry) {
            dispatcher.dispatch(new EnterPageEvent("wesib:enterPage" /* EnterPage */, {
                when: popState.state != null ? 'return' : 'enter',
                to: entry.page,
            }));
        }
    });
    dispatcher.on('hashchange')(() => {
        const entry = navHistory.hashChange(nav);
        if (entry) {
            dispatcher.dispatch(new EnterPageEvent("wesib:enterPage" /* EnterPage */, {
                when: 'enter',
                to: entry.page,
            }));
        }
    });
    class Navigation$ extends Navigation {
        constructor() {
            super();
            this.onEnter = dispatcher.on("wesib:enterPage" /* EnterPage */);
            this.onLeave = dispatcher.on("wesib:leavePage" /* LeavePage */);
            this.onStay = dispatcher.on("wesib:stayOnPage" /* StayOnPage */);
            this.on = onAny(onSupplied(this.onEnter), onSupplied(this.onLeave), onSupplied(this.onStay));
            this.read = nav.read.do(mapAfter(({ page }) => page));
        }
        get page() {
            return nav.it.page;
        }
        get length() {
            return history.length;
        }
        go(delta) {
            history.go(delta);
        }
        open(target) {
            return navigate('pre-open', 'open', target);
        }
        replace(target) {
            return navigate('pre-replace', 'replace', target);
        }
        update(url) {
            return navHistory.update(nav, toURL(url)).page;
        }
        with(ref, input) {
            return withParam(page => page.put(ref, input));
        }
    }
    return new Navigation$();
    function withParam(applyParams) {
        return {
            with(ref, input) {
                return withParam(mergeFunctions(applyParams, page => page.put(ref, input)));
            },
            open(target) {
                return navigate('pre-open', 'open', target, applyParams);
            },
            replace(target) {
                return navigate('pre-replace', 'replace', target, applyParams);
            },
            pretend(targetOrCallback, callback = (_from, to) => to) {
                let target;
                if (typeof targetOrCallback === 'function') {
                    callback = targetOrCallback;
                    target = undefined;
                }
                else {
                    target = targetOrCallback;
                }
                const navTarget = navTargetOf(target);
                const fromEntry = nav.it;
                const toEntry = newEntry('pretend', fromEntry, navTarget, applyParams);
                try {
                    return applyAgent('pretend', fromEntry, navTarget, toEntry)
                        ? callback(fromEntry.page, toEntry.page)
                        : undefined;
                }
                finally {
                    toEntry.stay(nav.it.page);
                }
            },
        };
    }
    function toURL(url) {
        if (typeof url === 'string') {
            return new URL(url, document.baseURI);
        }
        return url || nav.it.page.url;
    }
    function navTargetOf(target) {
        if (target == null || typeof target === 'string' || target instanceof URL) {
            return { url: toURL(target) };
        }
        if (target.url instanceof URL) {
            return target;
        }
        return { ...target, url: toURL(target.url) };
    }
    function navigate(whenLeave, when, target, applyParams = noop) {
        const navTarget = navTargetOf(target);
        const promise = next = next.then(doNavigate, doNavigate);
        return promise;
        function doNavigate() {
            let toEntry = undefined;
            try {
                const prepared = prepare();
                if (!prepared) {
                    return prepared; // Navigation cancelled
                }
                toEntry = prepared;
                navHistory[when](toEntry, nav);
                dispatcher.dispatch(new EnterPageEvent("wesib:enterPage" /* EnterPage */, {
                    when,
                    to: toEntry.page,
                }));
                return toEntry.page;
            }
            catch (e) {
                stay(toEntry, e);
                throw e;
            }
        }
        function prepare() {
            if (next !== promise) {
                return stay();
            }
            const fromEntry = nav.it;
            const toEntry = newEntry(whenLeave, fromEntry, navTarget, applyParams);
            const leavePage = new LeavePageEvent("wesib:leavePage" /* LeavePage */, {
                when: whenLeave,
                from: fromEntry.page,
                to: toEntry.page,
            });
            if (!dispatcher.dispatch(leavePage)
                || next !== promise
                || !applyAgent(whenLeave, fromEntry, navTarget, toEntry)) {
                return stay(toEntry);
            }
            return toEntry;
        }
        function stay(toEntry, reason) {
            if (toEntry) {
                toEntry.stay(nav.it.page);
            }
            dispatcher.dispatch(new StayOnPageEvent("wesib:stayOnPage" /* StayOnPage */, {
                from: nav.it.page,
                to: navTarget,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                reason,
            }));
            return null;
        }
    }
    function newEntry(whenLeave, fromEntry, navTarget, applyParams) {
        const toEntry = navHistory.newEntry(navTarget);
        try {
            fromEntry.transfer(toEntry, whenLeave);
            applyParams(toEntry.page);
        }
        catch (e) {
            toEntry.stay(nav.it.page);
            throw e;
        }
        return toEntry;
    }
    function applyAgent(whenLeave, fromEntry, navTarget, toEntry) {
        let navigated = false;
        agent(({ url, data, title }) => {
            navigated = true;
            navTarget.url = url;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            navTarget.data = data;
            navTarget.title = title;
        }, whenLeave, fromEntry.page, toEntry.page);
        return navigated;
    }
}

const NavElement$activeClass = ['active', Wesib__NS];
function navElement(element, options) {
    const getHref = options.href.bind(options);
    const events = setOfElements(options.event || 'click');
    const { active = NavElement$activeClass } = options;
    let activeClass;
    return owner => {
        const anchor = valueByRecipe(element, owner);
        if (!anchor) {
            return;
        }
        const { context, supply: ownerSupply = context.supply } = owner;
        activeClass = css__naming.name(active, context.get(NamespaceAliaser));
        const navigation = context.get(Navigation);
        const scheduler = context.get(ComponentRenderScheduler);
        const schedule = scheduler({ node: anchor });
        const supply = new Supply().needs(ownerSupply);
        const handleClick = {
            supply,
            receive(_ctx, event) {
                const href = getHref(anchor);
                const pageURL = navigation.page.url;
                const url = new URL(href, anchor.ownerDocument.baseURI);
                if (url.origin !== pageURL.origin) {
                    return; // External link
                }
                event.preventDefault();
                if (pageURL.href !== url.href) {
                    navigation.open(href).catch(console.error);
                }
            },
        };
        const eventDispatcher = new DomEventDispatcher(anchor);
        supply.cuts(eventDispatcher);
        for (const event of events) {
            eventDispatcher.on(event)(handleClick);
        }
        const css = drekCssClassesOf(anchor)
            .renderIn(deriveDrekContext(drekContextOf(anchor), {
            scheduler: _opts => schedule,
        }));
        return ({
            get href() {
                return getHref(anchor);
            },
            supply,
            activate() {
                return css.add(activeClass);
            },
        });
    };
}

function navAnchor(element, options = {}) {
    return navElement(element, {
        ...options,
        href(element) {
            return element.href;
        },
    });
}

const NavMenu$Links__symbol = ( /*#__PURE__*/Symbol('NavMenu.links'));
/**
 * Navigation menu.
 *
 * Serves as an {@link NavLink.Owner owner} of navigation links. Activates the links matching {@link Navigation.page
 * current page}.
 */
class NavMenu {
    /**
     * Constructs navigation menu.
     *
     * @param links - Navigation links of this menu. Either an iterable of navigation links or their providers,
     * an `AfterEvent` keeper of the same, or a function accepting component context and this menu as parameters,
     * and returning one of the above.
     * @param options - Additional options.
     */
    constructor(links, options) {
        this[NavMenu$Links__symbol] = new NavMenu$Links(this, links, options);
    }
    /**
     * Navigation menu supply.
     */
    get supply() {
        return this[NavMenu$Links__symbol].supply;
    }
    /**
     * Binds this menu to the given context.
     *
     * Subsequent calls have no effect.
     *
     * @param context - Owning component context.
     *
     * @returns `this` instance.
     */
    bindTo(context) {
        this[NavMenu$Links__symbol].bindTo(context);
        return this;
    }
}
class NavMenu$Links {
    constructor(_menu, _navLinks, options = {}) {
        this._menu = _menu;
        this._navLinks = _navLinks;
        this._links = trackValue([new Set()]);
        this._active = new Map();
        this.supply = new Supply().cuts(this._links);
        const { activate = true, weigh } = options;
        this._activate = activate;
        this._weigh = weigh ? weigh.bind(options) : defaultNavLinkWeight;
    }
    bindTo(context) {
        this.bindTo = noop;
        context.whenConnected(context => {
            let afterLinks;
            if (isAfterEvent(this._navLinks)) {
                afterLinks = this._navLinks;
            }
            else {
                const linkValues = valueByRecipe(this._navLinks, context, this._menu);
                afterLinks = isAfterEvent(linkValues)
                    ? linkValues
                    : afterThe(linkValues).do(translateAfter_((send, links) => send(...links)));
            }
            const owner = {
                context,
                supply: this.supply,
            };
            afterLinks((...links) => {
                this._replace(owner, links);
            });
            if (this._activate) {
                const navigation = context.get(Navigation);
                afterAll({
                    page: navigation,
                    links: this._links,
                })(({ page: [page], links: [[links]], }) => {
                    this._updateActive(context, page, links);
                });
            }
        });
    }
    _replace(owner, replacement) {
        var _a;
        const toAdd = new Set();
        for (const linkOrProvider of replacement) {
            const link = valueByRecipe(linkOrProvider, owner);
            if (link) {
                toAdd.add(link);
            }
        }
        const [links] = this._links.it;
        const toRemove = [];
        for (const link of links.keys()) {
            if (!toAdd.delete(link)) {
                toRemove.push(link);
            }
        }
        if (toAdd.size || toRemove.length) {
            for (const removed of toRemove) {
                links.delete(removed);
                this._deactivate(removed);
                (_a = removed.supply) === null || _a === void 0 ? void 0 : _a.off();
            }
            for (const added of toAdd) {
                const { supply } = added;
                if (supply) {
                    if (supply.isOff) {
                        continue; // Exclude disabled nav link.
                    }
                    supply.needs(this).whenOff(() => {
                        // Handle nav link removal
                        const [links] = this._links.it;
                        if (links.delete(added)) {
                            this._links.it = [links];
                        }
                    });
                }
                links.add(added);
            }
            this._links.it = [links];
        }
    }
    _updateActive(context, page, links) {
        const toDeactivate = [];
        const toActivate = this._selectActive(context, page, links);
        for (const link of this._active.keys()) {
            if (!toActivate.delete(link)) {
                toDeactivate.push(link);
            }
        }
        for (const deactivated of toDeactivate) {
            this._deactivate(deactivated);
        }
        for (const activated of toActivate) {
            if (activated.activate) {
                this._active.set(activated, activated.activate());
            }
        }
    }
    _selectActive(context, page, links) {
        let maxWeight = 0;
        let active = new Set();
        for (const link of links) {
            const weight = this._weigh({ link, menu: this._menu, context, page });
            if (weight > maxWeight) {
                maxWeight = weight;
                active = new Set().add(link);
            }
            else if (weight === maxWeight) {
                active.add(link);
            }
        }
        return active;
    }
    _deactivate(link) {
        const supply = this._active.get(link);
        if (supply) {
            this._active.delete(link);
            supply.off();
        }
    }
}
function defaultNavLinkWeight({ link, context, page, }) {
    const href = link.href;
    const linkURL = new URL(href, context.get(BootstrapWindow).document.baseURI);
    return calcNavLinkWeight(linkURL, page.url);
}
function calcNavLinkWeight(linkURL, pageURL) {
    if (linkURL.origin !== pageURL.origin) {
        return -1;
    }
    const linkDir = navLinkPath2dir(linkURL);
    const pageDir = navLinkPath2dir(pageURL);
    if (linkURL.hash) {
        if (linkDir !== pageDir) {
            return -1;
        }
        // Require search parameters to be equal
        const searchParamWeight = navLinkSearchParamsWeight(linkURL, pageURL);
        if (searchParamWeight < 0 || navLinkSearchParamsWeight(pageURL, linkURL) < 0) {
            return -1;
        }
        return linkURL.pathname.length
            + searchParamWeight
            + calcNavLinkWeight(getHashURL(linkURL), getHashURL(pageURL));
    }
    const searchParamWeight = navLinkSearchParamsWeight(linkURL, pageURL);
    if (searchParamWeight) {
        if (searchParamWeight < 0) {
            return -1;
        }
        if (linkDir !== pageDir) {
            return -1;
        }
        return linkURL.pathname.length + searchParamWeight;
    }
    if (!pageDir.startsWith(linkDir)) {
        return -1;
    }
    return linkURL.pathname.length;
}
function navLinkPath2dir(url) {
    const path = url.pathname;
    return path.endsWith('/') ? path : path + '/';
}
function navLinkSearchParamsWeight({ searchParams: linkParams }, { searchParams: pageParams }) {
    let weight = 0;
    linkParams.forEach((value, key) => {
        if (!isIgnoredSearchParam(key)) {
            if (weight >= 0) {
                if (pageParams.getAll(key).includes(value)) {
                    weight += 1;
                }
                else {
                    weight = -1;
                }
            }
        }
    });
    return weight;
}
function isIgnoredSearchParam(key) {
    return key.startsWith('__') && key.endsWith('__');
}

/**
 * Context value entry containing an {@link PageLoadAgent} instance.
 *
 * The agent returned combines all registered agents into one. If no agent registered it just performs the fetch.
 */
const PageLoadAgent = {
    perContext: ( /*#__PURE__*/cxFetchAgent()),
    toString: () => '[PageLoadAgent]',
};

/**
 * @internal
 */
function cachingPageLoader(loader) {
    let state;
    return page => {
        const url = pageUrl(page);
        if (state) {
            if (state.url === url) {
                return state.on;
            }
            state.sup.off();
        }
        let tracked;
        const supply = new Supply(() => {
            state = undefined;
            tracked = undefined;
        });
        const on = onEventBy(receiver => {
            if (!tracked) {
                const onLoad = loader(page);
                const tracker = trackValue();
                const trackSupply = onLoad(resp => {
                    tracker.it = resp;
                }).whenOff(reason => {
                    // Error drops page cache, unlike successful page load.
                    if (reason != null) {
                        supply.off(reason);
                    }
                });
                supply.cuts(trackSupply).cuts(tracker);
                tracked = {
                    on: tracker.read.do(valueOn_(asis)),
                    num: 0,
                };
            }
            const requested = tracked;
            ++requested.num;
            return requested.on.do(supplyOn(supply))(receiver).whenOff(reason => {
                if (!--requested.num) {
                    // Allow to request the same page again
                    Promise.resolve().then(() => {
                        if (!requested.num && requested === tracked) {
                            supply.off(reason);
                        }
                    }).catch(console.error);
                }
            });
        });
        state = { url, on, sup: supply };
        return on;
    };
}
function pageUrl(page) {
    return new URL('', page.url).href;
}

class PageLoadAbortError extends Error {
}
class PageLoadRequestsParam$ extends PageParam {
    create(_page, requests) {
        return {
            get() {
                return requests;
            },
            put: noop,
        };
    }
}
/**
 * @internal
 */
const PageLoadRequestsParam = new PageLoadRequestsParam$();
/**
 * @internal
 */
class PageLoadRequests {
    constructor(_navigation, _loader) {
        this._navigation = _navigation;
        this._loader = _loader;
        this._map = new Map();
        this._requests = flatMapIt(overIterator(() => this._map.values()));
    }
    get fragments() {
        const result = [];
        if (!itsEvery(this._requests, request => {
            if (!request.fragment) {
                return false;
            }
            result.push(request.fragment);
            return true;
        })) {
            return [];
        }
        return result;
    }
    handle() {
        const self = this;
        const pageSupply = new Supply(noop);
        let loadSupply = neverSupply();
        return {
            get() { },
            put(request) {
                self._add(request);
            },
            transfer(to, when) {
                if (when === 'pretend') {
                    return;
                }
                const transferred = self._transfer();
                to.put(PageLoadRequestsParam, transferred);
                return transferred.handle();
            },
            enter(page, when) {
                if (when === 'init') {
                    // The page is loaded already. No need to fetch it.
                    return;
                }
                loadSupply = new Supply(noop).needs(pageSupply);
                const onLoad = onEventBy(responseReceiver => {
                    const emitter = new EventEmitter();
                    const supply = emitter.on(responseReceiver);
                    self._loader(page).do(supplyOn(loadSupply))(response => emitter.send(response)).whenOff(error => {
                        if (error !== undefined && !(error instanceof PageLoadAbortError)) {
                            // Report current page load error as failed load response
                            emitter.send({
                                ok: false,
                                page,
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                error,
                            });
                        }
                    });
                    return supply;
                }).do(shareOn);
                itsEach(self._requests, ({ fragment, receiver }) => onFragment(onLoad, fragment)({
                    supply: new Supply().needs(receiver.supply),
                    receive(context, response) {
                        receiver.receive(context, response);
                    },
                }));
            },
            leave() {
                loadSupply.off(new PageLoadAbortError('page left'));
            },
            stay() {
                pageSupply.off(new PageLoadAbortError('navigation cancelled'));
            },
            forget() {
                pageSupply.off(new PageLoadAbortError('page forgotten'));
            },
        };
    }
    _add(request) {
        const req = { ...request, receiver: eventReceiver(request.receiver) };
        const { supply } = req.receiver;
        const list = this._map.get(supply);
        if (list) {
            list.push(req);
        }
        else {
            this._map.set(supply, [req]);
            supply.whenOff(() => this._map.delete(supply));
        }
    }
    _transfer() {
        const transferred = new PageLoadRequests(this._navigation, this._loader);
        for (const [supply, list] of this._map.entries()) {
            transferred._map.set(supply, list.slice());
        }
        return transferred;
    }
}
function onFragment(onLoad, fragment) {
    return fragment
        ? onLoad.do(mapOn_(response => response.ok
            ? {
                ...response,
                fragment: (fragment.tag != null
                    ? response.document.getElementsByTagName(fragment.tag)[0]
                    : response.document.getElementById(fragment.id)) || undefined,
            }
            : response))
        : onLoad;
}

/**
 * Bootstrap context entry containing a page load URL modifier.
 *
 * Does not modify URL by default.
 */
const PageLoadURLModifier = {
    perContext: ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxRecent({
        create: asis,
        byDefault: valueProvider(noop),
        assign: ({ get, to }) => {
            const modifier = url => get()(url);
            return receiver => to((_, by) => receiver(modifier, by));
        },
    })))),
    toString: () => '[PageLoadURLModifier]',
};

const PageLoader = {
    perContext: ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
        byDefault: PageLoader$create,
    })))),
    toString: () => '[PageLoader]',
};
function PageLoader$create(target) {
    const window = target.get(BootstrapWindow);
    const httpFetch = target.get(HttpFetch);
    const modifyURL = target.get(PageLoadURLModifier);
    const agent = target.get(PageLoadAgent);
    const parser = new window.DOMParser();
    return page => {
        const url = new URL(page.url.href);
        modifyURL(url);
        const request = new Request(url.href, {
            mode: 'same-origin',
            credentials: 'same-origin',
            headers: new Headers({ Accept: 'text/html' }),
        });
        return onEventBy(receiver => agent(fetch, request)(receiver));
        function fetch(fetchRequest) {
            requestPageFragments(page, fetchRequest);
            return onEventBy(receiver => {
                const dispatcher = new EventNotifier();
                dispatcher.on(receiver);
                dispatcher.send({ page });
                httpFetch(fetchRequest).do(mapOn_(response => Promise.all([response, response.text()])), resolveOnOrdered, digOn_((...batch) => afterThe(...batch)), mapOn_(([response, text]) => {
                    if (!response.ok) {
                        return {
                            ok: false,
                            page,
                            response,
                            error: response.status,
                        };
                    }
                    try {
                        return {
                            ok: true,
                            page,
                            response,
                            document: parsePageDocument(parser, url, response, text),
                        };
                    }
                    catch (error) {
                        return {
                            ok: false,
                            page,
                            response,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            error,
                        };
                    }
                }))(receiver);
            });
        }
    };
}
function requestPageFragments(page, request) {
    var _a;
    const fragments = (_a = page.get(PageLoadRequestsParam)) === null || _a === void 0 ? void 0 : _a.fragments;
    if (fragments && fragments.length) {
        request.headers.set('Accept-Fragment', fragments.reduce((header, fragment) => (header ? header + ', ' : '') + (fragment.tag != null
            ? 'tag=' + hthvQuote(fragment.tag)
            : 'id=' + hthvQuote(fragment.id)), ''));
    }
}
function parsePageDocument(parser, url, response, text) {
    const doc = parser.parseFromString(text, hthvParse(response.headers.get('Content-Type') || 'text/html')[0].v);
    if (doc.head) {
        const base = doc.head.querySelector('base');
        if (base) {
            base.href = new URL(base.getAttribute('href'), url).href;
        }
        else {
            const newBase = doc.createElement('base');
            newBase.href = url.href;
            doc.head.appendChild(newBase);
        }
    }
    return doc;
}

class PageLoadParam$ extends PageParam {
    create(page, request, bsContext) {
        const requests = new PageLoadRequests(bsContext.get(Navigation), cachingPageLoader(bsContext.get(PageLoader)));
        const handle = requests.handle();
        page.put(PageLoadRequestsParam, requests);
        handle.put(request);
        return handle;
    }
}
/**
 * Page load parameter.
 *
 * Accepts a {@link PageLoadRequest page load request} as input.
 *
 * A page load is initiated whenever a page with new address is {@link Navigation.onEnter entered}.
 *
 * Page load won't be initiated if:
 * - page load parameter is not {@link Page.put} added,
 * - all added {@link PageLoadRequest.receiver response receiver}s supplies are cut off, or
 * - the entered page address is the the same one as previous one, except the hash,
 */
const PageLoadParam = ( /*#__PURE__*/new PageLoadParam$());

const PageCacheBuster$perContext = ( /*#__PURE__*/cxScoped(BootstrapContext, ( /*#__PURE__*/cxSingle({
    byDefault: target => new PageCacheBuster(target),
}))));
const appRevSearchParam = '__wesib_app_rev__';
class PageCacheBuster {
    constructor(context) {
        const rev = appRev(context.get(BootstrapWindow).document);
        if (!rev) {
            this.urlModifier = undefined;
            this.agent = undefined;
        }
        else {
            const navigation = context.get(Navigation);
            this.urlModifier = url => url.searchParams.set(appRevSearchParam, rev);
            this.agent = (next, request) => next(new Request(request.url, request)).do(mapOn_(response => {
                if (response.ok) {
                    const newRev = appRev(response.document);
                    if (newRev && newRev !== rev) {
                        const url = new URL(response.page.url.href);
                        url.searchParams.set(appRevSearchParam, newRev);
                        navigation.update(url);
                        navigation.reload();
                    }
                }
                return response;
            }));
        }
    }
    static perContext(target) {
        return PageCacheBuster$perContext(target);
    }
    static toString() {
        return '[PageCacheBuster]';
    }
}
function appRev(doc) {
    var _a;
    return (_a = doc.querySelector('meta[name=wesib-app-rev]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content');
}

function pageScriptsAgent(context) {
    const doc = context.get(BootstrapWindow).document;
    return next => next().do(mapOn_(response => {
        if (response.ok) {
            const allScripts = new Set(mapIt(externalScripts(doc, doc.scripts), ([src]) => src));
            itsEach(filterIt(externalScripts(response.document, response.document.querySelectorAll('script')), ([src]) => !allScripts.has(src)), ([src, script]) => {
                importNode(script, doc.head, (_from, to) => to.src = src);
                allScripts.add(src);
            });
        }
        return response;
    }));
}
function externalScripts(doc, scripts) {
    return mapIt(filterArray(scripts, ({ src }) => !!src), script => [new URL(script.src, doc.baseURI).href, script]);
}

function pageStyleAgent(context) {
    const doc = context.get(BootstrapWindow).document;
    return next => next().do(mapOn_(response => {
        if (!response.ok) {
            return response;
        }
        const newStyles = response.document.querySelectorAll('link[rel=stylesheet]');
        if (!newStyles.length) {
            return response;
        }
        let target = doc.head;
        let before = null;
        const oldStyles = doc.querySelectorAll('link[rel=stylesheet]');
        const oldStylesByHref = new Map();
        const firstOldStyle = oldStyles.item(0);
        if (firstOldStyle) {
            target = firstOldStyle.parentNode;
            before = firstOldStyle;
            itsEach(overArray(oldStyles), link => oldStylesByHref.set(new URL(link.href, doc.baseURI).href, link));
        }
        itsEach(overArray(newStyles), newStyle => {
            const href = new URL(newStyle.href, doc.baseURI).href;
            const oldStyle = oldStylesByHref.get(href);
            if (oldStyle) {
                // Style already exists.
                if (itsFirst(oldStylesByHref.keys()) === href) {
                    // In proper position already.
                    // Insert the remaining styles after it.
                    target = oldStyle.parentNode;
                    before = oldStyle.nextSibling;
                }
                else {
                    // Move to proper position.
                    target.insertBefore(oldStyle, before);
                }
                oldStylesByHref.delete(href);
            }
            else {
                // Import new style
                importNode(newStyle, target, before, (_from, to) => to.href = href);
            }
        });
        // Remove remaining old styles
        itsEach(oldStylesByHref.values(), style => style.parentNode.removeChild(style));
        return response;
    }));
}

function pageTitleAgent(context) {
    const doc = context.get(BootstrapWindow).document;
    return next => next().do(mapOn_(response => {
        if (response.ok) {
            const title = response.document.getElementsByTagName('title').item(0);
            if (title && title.textContent) {
                doc.title = title.textContent;
            }
        }
        return response;
    }));
}

const PageLoadSupport__feature = {
    setup(setup) {
        setup.provide({
            entry: PageLoadURLModifier,
            buildAsset(target) {
                const { urlModifier } = target.get(PageCacheBuster);
                return urlModifier && (collector => collector(urlModifier));
            },
        });
        setup.provide({
            entry: PageLoadAgent,
            buildAsset(target) {
                const { agent } = target.get(PageCacheBuster);
                return agent && (collector => collector(agent));
            },
        });
        setup.provide({
            entry: PageLoadAgent,
            buildAsset(target) {
                const agent = pageScriptsAgent(target);
                return collector => collector(agent);
            },
        });
        setup.provide({
            entry: PageLoadAgent,
            buildAsset(target) {
                const agent = pageStyleAgent(target);
                return collector => collector(agent);
            },
        });
        setup.provide({
            entry: PageLoadAgent,
            buildAsset(target) {
                const agent = pageTitleAgent(target);
                return collector => collector(agent);
            },
        });
    },
};
/**
 * Page load support feature.
 *
 * Enables default {@link PageLoadAgent page load agents}:
 * 1. Page cache busting agent.
 *    If `<meta name="wesib-app-rev">` tag is present in initial page, then sends this tag's content with each page load
 *    request as `__wesib_app_rev__` search parameter. If the loaded page contains the same named meta tag with
 *    different content, then reloads the page.
 *    This serves both as cache busting technique, and as server-side application updates handler.
 * 2. Scripts agent.
 *    Includes external scripts from loaded page into main document.
 * 3. Style agent.
 *    Replaces external styles of main document styles with the ones from loaded page.
 *    Unless loaded page has no external styles.
 * 4. Title agent.
 *    Applies loaded page title to bootstrap window. If there is one.
 */
class PageLoadSupport {
    static get [FeatureDef__symbol]() {
        return PageLoadSupport__feature;
    }
}

/**
 * Component context entry containing {@link PageRenderCtl page render control}.
 */
const PageRenderCtl = {
    perContext: ( /*#__PURE__*/cxSingle({
        byDefault: target => new PageRenderCtl$(target.get(ComponentContext)),
    })),
    toString: () => '[PageRenderCtl]',
};
class PageRenderCtl$ {
    constructor(_context) {
        this._context = _context;
    }
    renderPageBy(renderer, def = {}) {
        const spec = valueByRecipe(def, this._context);
        const { contentKey = RenderPage$contentKey$default } = spec;
        const detectFragment = RenderPage$fragmentDetector(spec);
        const navigation = this._context.get(Navigation);
        const renderCtl = this._context.get(FragmentRenderCtl);
        let lastPageKey;
        const responseTracker = trackValue();
        const handleResponse = (response) => {
            const pageKey = contentKey(response.page);
            if (pageKey === lastPageKey) {
                return; // Only hash changed? Do not refresh the page.
            }
            responseTracker.it = [response, pageKey];
        };
        const supply = renderCtl.renderFragmentBy(fragExec => {
            const responseAndKey = responseTracker.it;
            if (!responseAndKey) {
                fragExec.retainContent();
                return;
            }
            const [response, pageKey] = responseAndKey;
            const exec = {
                ...fragExec,
                postpone(postponed) {
                    fragExec.postpone(() => postponed(exec));
                },
                response,
            };
            if (response.ok) {
                lastPageKey = pageKey;
                const { fragment } = response;
                if (fragment) {
                    importNodeContent(fragment, fragExec.content);
                }
            }
            renderer(exec);
        }, {
            ...spec,
            when: 'connected',
            on: responseTracker.on,
        });
        this._context.whenConnected(context => {
            lastPageKey = contentKey(navigation.page);
            navigation.read.do(onceAfter)(page => {
                page.put(PageLoadParam, {
                    fragment: detectFragment(context),
                    receiver: {
                        supply: new Supply().needs(supply),
                        receive: (_ctx, response) => handleResponse(response),
                    },
                });
            });
        });
        return supply;
    }
}
function RenderPage$fragmentDetector({ fragment }) {
    if (fragment) {
        return _context => fragment;
    }
    return ({ element: { id, tagName }, }) => id
        ? { id }
        : { tag: tagName.toLowerCase() };
}
function RenderPage$contentKey$default({ url }) {
    return new URL('', url).href;
}

/**
 * Creates a {@link RenderPageDef.Method page renderer} method amendment (and decorator).
 *
 * Renders pages using {@link PageRenderCtl page render control}.
 *
 * Utilizes {@link PageLoadParam} navigation parameter.
 *
 * Enables {@link PageLoadSupport} feature.
 *
 * @typeParam TClass - Amended component class type.
 * @typeParam TAmended - Amended component member entity type.
 * @param def - Page inclusion definition.
 *
 * @returns New component method amendment.
 */
function RenderPage(def) {
    return ComponentMember(({ get, amend }) => amend({
        componentDef: {
            feature: {
                needs: [PageLoadSupport],
            },
            define(defContext) {
                defContext.whenComponent((context) => {
                    context.whenReady(context => {
                        const { component } = context;
                        const renderer = get(component).bind(component);
                        context.get(PageRenderCtl).renderPageBy(renderer, def);
                    });
                });
            },
        },
    }));
}

export { EnterPageEvent, LeavePageEvent, NavMenu, Navigation, NavigationAgent, PageHashURLParam, PageHashURLSupport, PageLoadAgent, PageLoadParam, PageLoadSupport, PageLoadURLModifier, PageParam, PageParam__symbol, PageRenderCtl, RenderPage, StayOnPageEvent, getHashURL, navAnchor, navElement, setHashURL };
//# sourceMappingURL=wesib.navigation.js.map
